import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, CreditCard, Building2, Bell, Dumbbell, Activity, Award, BookOpen, Utensils, LogOut, Menu, X, Inbox, CircleDollarSign } from 'lucide-react';
import { Logo, Avatar, Badge, cn } from '@techsofit/ui';
import { useAuth } from '@techsofit/app-kit';

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  end?: boolean;
}

const NAV: NavItem[] = [
  { to: '/', label: 'Overview', icon: LayoutDashboard, end: true },
  { to: '/leads', label: 'Leads', icon: Inbox },
  { to: '/plans', label: 'Subscription plans', icon: CreditCard },
  { to: '/programs', label: 'Programs', icon: Dumbbell },
  { to: '/exercises', label: 'Exercises', icon: Activity },
  { to: '/badges', label: 'Badges', icon: Award },
  { to: '/learn', label: 'Learn', icon: BookOpen },
  { to: '/nutrition', label: 'Nutrition', icon: Utensils },
  { to: '/billing', label: 'Gyms & billing', icon: Building2 },
  { to: '/revenue', label: 'Revenue', icon: CircleDollarSign },
  { to: '/settings', label: 'Notifications', icon: Bell },
];

function SidebarLink({ item, onNavigate }: { item: NavItem; onNavigate?: () => void }) {
  const { to, label, icon: Icon, end } = item;
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'group flex items-center gap-3 rounded-control px-3.5 py-2.5 text-sm font-medium transition-colors duration-150',
          isActive ? 'bg-accent text-accent-ink' : 'text-on-dark-muted hover:bg-dark-soft hover:text-on-dark',
        )
      }
    >
      <Icon className="size-5 shrink-0" aria-hidden />
      {label}
    </NavLink>
  );
}

/** The dark nav panel — shared by the desktop sidebar and the mobile drawer. */
function NavPanel({ displayName, onNavigate, onLogout }: { displayName: string; onNavigate?: () => void; onLogout: () => void }) {
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between px-1.5 py-2">
        <Logo onDark />
        <Badge tone="accent">Admin</Badge>
      </div>

      <nav className="mt-6 flex flex-col gap-1">
        {NAV.map((item) => (
          <SidebarLink key={item.to} item={item} onNavigate={onNavigate} />
        ))}
      </nav>

      <div className="mt-auto flex items-center gap-3 rounded-control bg-dark-soft p-2.5">
        <Avatar name={displayName} size="sm" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-on-dark">{displayName}</p>
          <p className="truncate text-xs text-on-dark-muted">Platform admin</p>
        </div>
        <button
          onClick={onLogout}
          aria-label="Sign out"
          className="inline-flex size-8 items-center justify-center rounded-control text-on-dark-muted hover:bg-dark-line hover:text-on-dark"
        >
          <LogOut className="size-4" aria-hidden />
        </button>
      </div>
    </div>
  );
}

/** Desktop: dark rounded sidebar + main area (matches gym-web). Mobile: top bar + slide-in drawer. */
export function AdminShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  const displayName = user?.fullName ?? '';

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMenuOpen(false);
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  async function handleLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  return (
    <div className="min-h-dvh bg-canvas">
      <div className="mx-auto flex max-w-[1400px] gap-3 p-3 md:gap-4 md:p-4">
        {/* Desktop sidebar */}
        <aside className="sticky top-4 hidden h-[calc(100dvh-2rem)] w-60 shrink-0 rounded-card bg-dark p-4 md:block">
          <NavPanel displayName={displayName} onLogout={handleLogout} />
        </aside>

        {/* Main content */}
        <main className="min-w-0 flex-1">
          {/* Mobile top bar */}
          <header className="mb-3 flex items-center justify-between md:hidden">
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
              aria-expanded={menuOpen}
              className="inline-flex size-touch items-center justify-center rounded-control bg-card text-ink shadow-card active:scale-95"
            >
              <Menu className="size-5" aria-hidden />
            </button>
            <Logo />
            <Avatar name={displayName} size="sm" />
          </header>

          <Outlet />
        </main>
      </div>

      {/* Mobile drawer */}
      {menuOpen && (
        <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true" aria-label="Menu">
          <button type="button" aria-label="Close menu" onClick={() => setMenuOpen(false)} className="absolute inset-0 bg-ink/40 backdrop-blur-sm animate-fade-in" />
          <div className="absolute inset-y-0 left-0 flex w-[17rem] max-w-[82vw] flex-col bg-dark p-4 shadow-pop animate-slide-in-left">
            <button
              type="button"
              onClick={() => setMenuOpen(false)}
              aria-label="Close menu"
              className="absolute right-3 top-3 inline-flex size-9 items-center justify-center rounded-control text-on-dark-muted hover:bg-dark-soft hover:text-on-dark"
            >
              <X className="size-5" aria-hidden />
            </button>
            <NavPanel displayName={displayName} onNavigate={() => setMenuOpen(false)} onLogout={handleLogout} />
          </div>
        </div>
      )}
    </div>
  );
}
